import React from 'react';
import { map } from 'lodash';
import classnames from 'classnames';
import { ReactComponent as DatabaseIcon } from './assets/database.svg';
import { CircularProgress } from '@mui/material';
import PropTypes from 'prop-types';
import TableList from './TableList';
import Typography from 'new/elements/Typography';
import { SearchInjector } from 'new/elements/SearchableProvider/SearchInjector';
import { ReactComponent as Expand } from 'js/components/elements/treeview/assets/expandMoreFilled.svg';
import cx from 'classnames';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { PROBE_GET_TREE_REQUEST } from 'js/constants/actionTypes';

class DatabaseItem extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isSelecting: false,
      tables: map(props.tablesForDatabase[props.d.id], (id) => props.tables[id]),
    };

    this._onSelect = this._onSelect.bind(this);
  }

  componentDidUpdate(prevProps, prevState) {
    const { d, probeDataFlat, tables, tablesForDatabase } = this.props;
    const tablesChanged =
      tables !== prevProps.tables || tablesForDatabase !== prevProps.tablesForDatabase;

    // Only clear isSelecting once both the probe is done AND the tables props have
    // been updated by the parent. Without both conditions, clearing on probeDataFlat
    // alone causes a flash of the empty-database message before tables arrive (the
    // parent's setState from its own componentDidUpdate lands one render later).
    if (this.state.isSelecting && probeDataFlat[d.id]?.scanned && tablesChanged) {
      this.setState({ isSelecting: false });
    }

    if (tablesChanged) {
      this.setState({
        tables: map(tablesForDatabase[d?.id], (id) => tables[id]),
      });
    }
  }

  render() {
    const {
      d,
      selectedDatabaseId,
      credentialId,
      openTableEditor,
      expandTable,
      expandedTableId,
      selectedTable,
      isNewTable,
      probeDataFlat,
      shouldShowExpandedView,
      connectionCategory,
      handleShowPreview,
      isProbingTree = false,
    } = this.props;
    const { isSelecting, tables } = this.state;
    const selected = d.id === selectedDatabaseId;

    return (
      <li
        className={classnames('database-node', {
          selected: selected,
        })}
      >
        <div
          className="database-row-content"
          onClick={this._onSelect}
          data-testid={`database-node-${d.id}`}
        >
          <>
            {isSelecting ? (
              <CircularProgress
                size={16}
                thickness={4}
                style={{ marginLeft: 5, marginRight: 6 }}
                color="primary"
                data-testid="database-name-loader"
              />
            ) : (
              <div className="expand-icon-button">
                <Expand
                  className={cx('expand', {
                    ['expand-opened']: selected,
                    ['expand-closed']: !selected,
                  })}
                />
              </div>
            )}
          </>
          <div className="database-node-detail">
            <div
              className={classnames('database-item', {
                selected: selected,
              })}
            >
              <DatabaseIcon className="database-icon" />
              <Typography variant="bodyLarge">{d.name}</Typography>
            </div>
          </div>
        </div>
        {selected && !isSelecting && (
          <SearchInjector>
            <TableList
              tables={tables}
              database={d}
              credentialId={credentialId}
              openTableEditor={openTableEditor}
              expandTable={expandTable}
              expandedTableId={expandedTableId}
              selectedTable={selectedTable}
              isNewTable={isNewTable}
              probeDataFlat={probeDataFlat}
              isProbingTree={isProbingTree}
              shouldShowExpandedView={shouldShowExpandedView}
              connectionCategory={connectionCategory}
              handleShowPreview={handleShowPreview}
            />
          </SearchInjector>
        )}
      </li>
    );
  }

  _onSelect(e) {
    const { d, selectedDatabaseId, probeDataFlat, selectDatabase } = this.props;

    e.stopPropagation();

    if ((probeDataFlat[d.id] && probeDataFlat[d.id].scanned) || d.id === selectedDatabaseId) {
      selectDatabase(d.id);
    } else {
      this.setState({ isSelecting: true }, () => {
        selectDatabase(d.id);
      });
    }
  }
}

export default function DatabaseList({
  databases,
  selectDatabase,
  selectedDatabaseId,
  tablesForDatabase,
  tables,
  credentialId,
  openTableEditor,
  expandTable,
  expandedTableId,
  selectedTable,
  isNewTable,
  probeDataFlat,
  shouldShowExpandedView = true,
  connectionCategory,
  handleShowPreview,
}) {
  const isProbingTree = useIsLoading(PROBE_GET_TREE_REQUEST);

  return (
    <ul className="database-list">
      {map(databases, (d) => (
        <DatabaseItem
          key={d.id}
          d={d}
          credentialId={credentialId}
          selectDatabase={selectDatabase}
          selectedDatabaseId={selectedDatabaseId}
          tablesForDatabase={tablesForDatabase}
          tables={tables}
          openTableEditor={openTableEditor}
          expandTable={expandTable}
          expandedTableId={expandedTableId}
          selectedTable={selectedTable}
          isNewTable={isNewTable}
          probeDataFlat={probeDataFlat}
          isProbingTree={isProbingTree}
          shouldShowExpandedView={shouldShowExpandedView}
          connectionCategory={connectionCategory}
          handleShowPreview={handleShowPreview}
        />
      ))}
    </ul>
  );
}
